import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Outlet, useLocation } from 'react-router-dom';
import { changeSidebarType } from 'redux/actions';
import { useRedux, useViewport } from 'hooks';
import { changeBodyAttribute } from 'utils';
import * as layoutConstants from 'appConstants';
import { uniqueId } from 'react-bootstrap-typeahead/types/utils';
import { LayoutProps } from '../routes';
import LoginHelper from 'components/LoginHelper';
import CrewManagerPopupModal from 'components/CrewManagerPopupModal';

const Topbar = React.lazy(() => import('./Topbar'));
const LeftSidebar = React.lazy(() => import('./LeftSidebar'));
const Footer = React.lazy(() => import('./Footer'));
const RightSidebar = React.lazy(() => import('./RightSidebar'));

const loading = () => <div className=""></div>;

const MENU_AUTO_CLOSE_TIMEOUT = 5000;

const resolveSidebarType = (viewportWidth: number) => {
    if (
        viewportWidth <= layoutConstants.MOBILE_MENU_BREAKPOINT ||
        viewportWidth > layoutConstants.WIDE_SIDEBAR_BREAKPOINT
    ) {
        return layoutConstants.SideBarWidth.LEFT_SIDEBAR_TYPE_FIXED;
    }
    return layoutConstants.SideBarWidth.LEFT_SIDEBAR_TYPE_CONDENSED;
};

const VerticalLayout: React.VFC<LayoutProps> = ({ menuAutoClosing }) => {
    const { dispatch, appSelector } = useRedux();
    const { width } = useViewport();
    const { pathname } = useLocation();
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [clickedOnMenu, setClickedOnMenu] = useState(0);

    const { layoutColor, leftSideBarTheme, leftSideBarType, layoutWidth, changedByUser } = appSelector((state) => ({
        layoutColor: state.auth.useDarkMode
            ? layoutConstants.LayoutColor.LAYOUT_COLOR_DARK
            : layoutConstants.LayoutColor.LAYOUT_COLOR_LIGHT,
        layoutWidth: state.Layout.layoutWidth,
        leftSideBarTheme: state.Layout.leftSideBarTheme,
        leftSideBarType: state.Layout.leftSideBarType,
        changedByUser: state.Layout.changedByUser,
    }));

    useEffect(() => {
        changeBodyAttribute('data-layout', layoutConstants.LayoutTypes.LAYOUT_VERTICAL);
    }, []);

    useEffect(() => {
        changeBodyAttribute('data-layout-color', layoutColor);
    }, [layoutColor]);

    useEffect(() => {
        changeBodyAttribute('data-layout-mode', layoutWidth);
    }, [layoutWidth]);

    useEffect(() => {
        changeBodyAttribute('data-leftbar-theme', leftSideBarTheme);
    }, [leftSideBarTheme]);

    useEffect(() => {
        // Condensed layout CSS only applies on desktop; mobile overlay uses sidebar-enable + fixed
        const compactMode =
            width <= layoutConstants.MOBILE_MENU_BREAKPOINT
                ? layoutConstants.SideBarWidth.LEFT_SIDEBAR_TYPE_FIXED
                : leftSideBarType;
        changeBodyAttribute('data-leftbar-compact-mode', compactMode);
    }, [leftSideBarType, width]);

    const closeMenu = useCallback(() => {
        document.body.classList.remove('sidebar-enable');
        document.body.style.overflow = '';
        setIsMenuOpened(false);
    }, []);

    const openMenu = () => {
        if (width > layoutConstants.MOBILE_MENU_BREAKPOINT) {
            return;
        }

        if (isMenuOpened) {
            closeMenu();
        } else {
            document.body.classList.add('sidebar-enable');
            document.body.style.overflow = 'hidden';
            setIsMenuOpened(true);
        }
    };

    useEffect(() => {
        if (width > layoutConstants.MOBILE_MENU_BREAKPOINT && isMenuOpened) {
            closeMenu();
        }
    }, [width, isMenuOpened, closeMenu]);

    useEffect(() => {
        closeMenu();
    }, [pathname, closeMenu]);

    useEffect(() => {
        return () => {
            document.body.classList.remove('sidebar-enable');
            document.body.style.overflow = '';
        };
    }, []);

    const updateDimensions = useCallback(() => {
        dispatch(changeSidebarType(resolveSidebarType(width)));
    }, [dispatch, width]);

    useEffect(() => {
        dispatch(changeSidebarType(resolveSidebarType(width)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.addEventListener('resize', updateDimensions);
        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [dispatch, updateDimensions]);

    const isCondensed = leftSideBarType === layoutConstants.SideBarWidth.LEFT_SIDEBAR_TYPE_CONDENSED;
    const isLight = leftSideBarTheme === layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_LIGHT;

    useEffect(() => {
        if (menuAutoClosing.current) {
            const autoClosingId = uniqueId();
            menuAutoClosing.current = menuAutoClosing.current ? autoClosingId : menuAutoClosing.current;

            setTimeout(() => {
                if (autoClosingId === menuAutoClosing.current && !changedByUser) {
                    menuAutoClosing.current = null;
                    if (
                        width > layoutConstants.MOBILE_MENU_BREAKPOINT &&
                        width <= layoutConstants.WIDE_SIDEBAR_BREAKPOINT
                    ) {
                        dispatch(changeSidebarType(layoutConstants.SideBarWidth.LEFT_SIDEBAR_TYPE_CONDENSED));
                    }
                } else if (changedByUser) {
                    menuAutoClosing.current = null;
                }
            }, MENU_AUTO_CLOSE_TIMEOUT);
        }
    }, [changedByUser, dispatch, menuAutoClosing, clickedOnMenu, width]);

    return (
        <>
            <LoginHelper />
            <CrewManagerPopupModal />
            <div className="wrapper">
                <Suspense fallback={loading()}>
                    <LeftSidebar
                        isCondensed={isCondensed}
                        isLight={isLight}
                        hideUserProfile={true}
                        setClickedOnMenu={setClickedOnMenu}
                        onMenuClose={closeMenu}
                    />
                </Suspense>
                <div className="content-page">
                    <div className="content">
                        <Suspense fallback={loading()}>
                            <Topbar openLeftMenuCallBack={openMenu} hideLogo={true} />
                        </Suspense>
                        <Container fluid>
                            <Outlet />
                        </Container>
                    </div>

                    <Suspense fallback={loading()}>
                        <Footer />
                    </Suspense>
                </div>
            </div>

            {/* Floating burger button – only visible on mobile, replaces topbar */}
            <button className="mobile-sidebar-toggle d-mobile-menu-only" onClick={openMenu} aria-label="Menü öffnen">
                <i className={isMenuOpened ? 'mdi mdi-close' : 'mdi mdi-menu'} />
            </button>

            {isMenuOpened && (
                <div className="mobile-sidebar-backdrop d-mobile-menu-only" onClick={closeMenu} aria-hidden="true" />
            )}

            <Suspense fallback={loading()}>
                <RightSidebar />
            </Suspense>
        </>
    );
};
export default VerticalLayout;
